import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';

import { Banner } from '@flintmint/web-shared/components/banner';

import { useOpenAiRefusalInvitation } from '@/features/billing/ai-refusal-invitation/ai-refusal-invitation';
import { cancelledTrialEnd, getEnergyView, isTrialEnergyUsedUp } from '@/features/llm-chat/energy/energy-view';
import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { useUserAccount } from '@/hooks/use-user-account';
import { cancelledTrialBannerDismissedAtom, trialEnergyBannerDismissedAtom } from '@/state';

import { formatBillingDayMonth } from '../billing-format';

/**
 * Asks a Trial whose early start is held to complete its payment, at any Trial Energy level and with no close
 * control. Otherwise tells a Trial whose Energy is used up that it is still active, and opens the choices for
 * starting its plan; or tells a cancelled one when it ends, and links to Subscription & Billing.
 */
export const TrialEnergyBanner = () => {
  const { account } = useUserAccount();
  const [dismissed, setDismissed] = useAtom(trialEnergyBannerDismissedAtom);
  const [cancelledDismissed, setCancelledDismissed] = useAtom(cancelledTrialBannerDismissedAtom);
  const openInvitation = useOpenAiRefusalInvitation();
  const { open: openStartPlan } = useUrlOverlay(urlOverlays.startPlan, urlOverlays);
  const navigate = useNavigate();

  if (getEnergyView(account)?.action === 'payHeldCharge') {
    return (
      <Banner
        type="neutral"
        title="Your early start needs payment"
        message="Your Trial and remaining Energy are unchanged. Complete payment to start your paid plan."
        action={{ label: 'Complete payment', onClick: openStartPlan }}
      />
    );
  }

  const trialEnd = cancelledTrialEnd(account);
  if (trialEnd) {
    if (cancelledDismissed) return null;
    return (
      <Banner
        type="neutral"
        title={`Your Trial ends on ${formatBillingDayMonth(trialEnd)}`}
        message="Your Trial Energy is used up. Your scheduled subscription is still cancelled, so no payment is scheduled."
        onClose={() => setCancelledDismissed(true)}
        action={{ label: 'Manage', onClick: () => void navigate(appRoutes.settingsBilling) }}
      />
    );
  }

  if (dismissed || !isTrialEnergyUsedUp(account)) return null;

  return (
    <Banner
      type="neutral"
      title="Your Trial is still active"
      message="Your Trial Energy is used up. Start your subscription now, or cancel the scheduled subscription and keep using Free."
      onClose={() => setDismissed(true)}
      action={{ label: 'Review options', onClick: openInvitation }}
    />
  );
};
